import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Wallet, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Input } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";
import { Card } from "@/components/ui-kit/Card";
import { Button } from "@/components/ui-kit/Button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";
import { PaymentDialog } from "@/components/payments/PaymentDialog";
import { PaymentStatusPill } from "@/components/payments/PaymentStatusPill";
import { useFunder } from "@/funder/FunderContext";

const METHODS = ["All", "Check", "ACH", "Card", "Cash", "Retainer"];

export function PaymentsPage() {
  const navigate = useNavigate();
  const { abbr, payerLabel } = useFunder();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [payer, setPayer] = useState("All");
  const [method, setMethod] = useState("All");
  const [status, setStatus] = useState("All");
  const [recordOpen, setRecordOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payments").then((r) => setRows(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (payer !== "All" && r.payer !== payer) return false;
      if (method !== "All" && r.method !== method) return false;
      if (status !== "All" && r.status !== status) return false;
      if (!q) return true;
      return (r.client_name || "").toLowerCase().includes(q)
        || (r.matter_name || "").toLowerCase().includes(q)
        || (r.reference || "").toLowerCase().includes(q);
    });
  }, [rows, query, payer, method, status]);

  const doVoid = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/${voidTarget.id}/void`);
      setVoidTarget(null);
      load();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">Payments</h1>
          <p className="mt-2 text-sm text-muted-foreground">Every payment across all matters — recorded, never deleted.</p>
        </div>
        <Button onClick={() => setRecordOpen(true)} data-testid="record-payment-button">
          <Wallet className="h-4 w-4" /> Record payment
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="payment-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search client / matter / reference…" className="pl-9" />
        </div>
        <Select value={payer} onChange={(e) => setPayer(e.target.value)} data-testid="payment-payer-filter" className="max-w-[150px]">
          <option value="All">All payers</option>
          <option value="Client">Client</option>
          <option value="ACSA">{abbr}</option>
        </Select>
        <Select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method-filter" className="max-w-[150px]">
          {METHODS.map((m) => <option key={m} value={m}>{m === "All" ? "All methods" : m}</option>)}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="payment-status-filter" className="max-w-[150px]">
          <option value="All">All statuses</option>
          <option value="Active">Active</option>
          <option value="Void">Void</option>
        </Select>
      </div>

      <Card>
        {error ? (
          <div className="p-6 text-sm text-destructive">{error}</div>
        ) : rows === null ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-lg bg-secondary text-muted-foreground"><Wallet className="h-6 w-6" /></span>
            <p className="text-sm text-muted-foreground">No payments match.</p>
          </div>
        ) : (
          <Table>
            <THead>
              <TR><TH>Date</TH><TH>Payer</TH><TH>Client / Matter</TH><TH className="text-right">Amount</TH><TH>Method</TH><TH>Reference</TH><TH>Invoice</TH><TH>Status</TH><TH /></TR>
            </THead>
            <TBody>
              {filtered.map((p) => (
                <TR key={p.id} data-testid={`payment-row-${p.id}`}>
                  <TD className="whitespace-nowrap text-muted-foreground">{shortDate(p.date)}</TD>
                  <TD className="text-muted-foreground">{payerLabel(p.payer)}</TD>
                  <TD>
                    <button className="text-foreground hover:underline" onClick={() => navigate(`/workbench/${p.matter_id}`)} data-testid={`payment-matter-link-${p.id}`}>
                      {p.client_name || "—"} · {p.matter_name || "—"}
                    </button>
                  </TD>
                  <TD className="nums text-right font-medium text-foreground">{money(p.amount)}</TD>
                  <TD className="text-muted-foreground">{p.method}</TD>
                  <TD className="text-muted-foreground">{p.reference || "—"}</TD>
                  <TD className="text-muted-foreground">{p.invoice_number || "—"}</TD>
                  <TD><PaymentStatusPill status={p.status} /></TD>
                  <TD className="text-right">
                    {p.status !== "Void" && (
                      <Button size="sm" variant="ghost" onClick={() => setVoidTarget(p)} data-testid={`void-payment-${p.id}`}>
                        <Ban className="h-3.5 w-3.5" /> Void
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <PaymentDialog open={recordOpen} onOpenChange={setRecordOpen} onDone={load} />

      <Dialog open={!!voidTarget} onOpenChange={(o) => { if (!busy && !o) setVoidTarget(null); }}>
        <DialogContent data-testid="payments-void-dialog">
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-status-void/10 text-status-void"><Ban className="h-4 w-4" /></span>
            Void this payment?
          </DialogTitle>
          <DialogDescription>
            {voidTarget && `Voiding ${money(voidTarget.amount)} reverses its balance effect and recomputes status. The record is kept and an audit event is written.`}
          </DialogDescription>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVoidTarget(null)} disabled={busy}>Cancel</Button>
            <Button onClick={doVoid} disabled={busy} data-testid="void-payment-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Void payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PaymentsPage;
